import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type RowClickedEvent, type RowClassParams } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, FormControl, MenuItem, Select, Tooltip } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

interface State {
}

const defState: State = {
};

interface SearchParams {
  checkDateFrom: string,
  checkDateTo: string,
  mngNameLike: string,
  locationIdLike: string,
  checkStatus: string,
  regNameLike: string,
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  checkDateFrom: dayjs().subtract(3, "month").format("YYYYMMDD"),
  checkDateTo: dayjs().format("YYYYMMDD"),
  mngNameLike: "",
  locationIdLike: "",
  checkStatus: "",
  regNameLike: "",
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

function InventoryAuditList(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "실사명", headerClass: "hd-ta-left", field: "mngName", width: 300, cellStyle: {textAlign: "left", cursor: "pointer"} },
    { headerName: "실사범위", headerClass: "hd-ta-left", field: "locationId", width: 300, cellStyle: {textAlign: "left", cursor: "pointer"},
      valueGetter: (p: any) => {
        if (p.node.rowPinned) return "합계";
        return (valueFormatter.locationId(p.data.startLocationId) + " ~ " + valueFormatter.locationId(p.data.endLocationId));
      }
    },
    { headerName: "진척률", headerClass: "hd-ta-right", field: "progRate", width: 100, cellStyle: {textAlign: "right", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]},
      valueGetter: (p: any) => {
        const progRate = p.data.targetItemQty > 0 ? Math.round((p.data.checkItemQty / p.data.targetItemQty) * 100) : 0;
        return (progRate + "%");
      }
    },
    { headerName: "실사상태", field: "checkStatus", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("checkStatus") },
    { headerName: "시작일", field: "startDate", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.date("yyyy-MM-dd"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "종료일", field: "endDate", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]},
      valueGetter: (p: any) => {
        if (p.node.rowPinned) return "";
        return (p.data.endDate != null && p.data.endDate != "" ? kutil.date2string(p.data.endDate, "yyyy-MM-dd") : "-");
      }
    },
    { headerName: "대상SKU", headerClass: "hd-ta-right", field: "targetSkuQty", width: 120, cellStyle: { textAlign: "right", cursor: "pointer"}, valueFormatter: valueFormatter.number, summable: true, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "대상ITEM", headerClass: "hd-ta-right", field: "targetItemQty", width: 120, cellStyle: { textAlign: "right", cursor: "pointer"}, valueFormatter: valueFormatter.number, summable: true, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "실사SKU", headerClass: "hd-ta-right", field: "checkSkuQty", width: 120, cellStyle: { textAlign: "right", cursor: "pointer"}, valueFormatter: valueFormatter.number, summable: true, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "실사ITEM", headerClass: "hd-ta-right", field: "checkItemQty", width: 120, cellStyle: { textAlign: "right", cursor: "pointer"}, valueFormatter: valueFormatter.number, summable: true, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "수량차이", headerClass: "hd-ta-right", field: "actTargetQty", width: 120, cellStyle: { textAlign: "right", cursor: "pointer"}, valueFormatter: valueFormatter.number, summable: true, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "등록자", field: "regName", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
  }), []);
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
    onRowClicked: (e: RowClickedEvent) => {
      if (!e.node.rowPinned) {
        showDetail(e.data);
      }
    },
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/stock/inventory_audit_list.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/stock/inventory_audit_list", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/stock/inventory_audit_list", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/stock/inventory_audit_list", {});
  }

  function showDetail(info: any) {
    router.navigateByPath("/stock/inventory_audit_detail", {
      stockCheckNo: info.stockCheckNo,
      searchParams: JSON.stringify(result.searchParams)
    });
  }

  function addDetail() {
    router.navigateByPath("/stock/inventory_audit_regist", {
      stockCheckNo: "",
      searchParams: JSON.stringify(result.searchParams)
    });
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj(result.searchParams, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/stock/inventory_audit_list.download?mode=search", searchParams, {
        "fileName": "재고실사_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents list">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>재고실사</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
            { auth.write &&
            <Button variant="contained" onClick={addDetail}>신규등록</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div className="date-range">
            <p>실시기간</p>
            <div>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD"
                  value={dayjs(searchParams.checkDateFrom)}
                  onChange={(e) => {doSearch(e, {checkDateFrom: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  maxDate={dayjs(searchParams.checkDateTo)}
                />
                -
                <DatePicker
                  sx={{ width: 180 }}
                  slotProps={{ textField: { size: "small" }, field: { readOnly: true } }}
                  format="YYYY-MM-DD"
                  value={dayjs(searchParams.checkDateTo)}
                  onChange={(e) => {doSearch(e, {checkDateTo: (e != null ? dayjs(e).format("YYYYMMDD") : "")})}}
                  minDate={dayjs(searchParams.checkDateFrom)}
                />
              </LocalizationProvider>
            </div>
          </div>
          <div>
            <p>실사명</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.mngNameLike}
              onChange={(e) => {updateSearchParams({ mngNameLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="실사명"
            />
          </div>
          <div>
            <p>실사범위</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.locationIdLike}
              onChange={(e) => {updateSearchParams({ locationIdLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="실사범위"
            />
          </div>
          <div>
            <p>실사상태</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.checkStatus}
                onChange={(e) => {doSearch(e, {checkStatus: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.checkStatus as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>등록자</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.regNameLike}
              onChange={(e) => {updateSearchParams({ regNameLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="등록자"
            />
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 재고실사 목록 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              onStateUpdated={gridCallbacks.onStateUpdated}
              onRowClicked={gridCallbacks.onRowClicked}
              totalPage={result.pageInfo.totalPage}
              currPage={result.pageInfo.page}
              onPageChange={goPage}
            />
          </div>
        </AgGridProvider>
        {/*// 재고실사 목록 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(InventoryAuditList);
